<template>
  <aside class="analysis-section-sidebar d-none d-md-block">
    <nav
      class="analysis-section-sidebar__inner"
      role="tablist"
      :aria-label="t('analysis.title')"
    >
      <button
        v-for="(tab, index) in tabs"
        :key="index"
        type="button"
        class="analysis-section-link"
        :class="{ 'analysis-section-link--active': modelValue === index }"
        role="tab"
        :aria-selected="modelValue === index"
        @click="selectTab(index)"
      >
        <v-icon :icon="tab.icon" class="analysis-section-link__icon" />
        <span class="analysis-section-link__label">{{ tab.title }}</span>
      </button>

      <div
        v-if="showLoadingStatus"
        class="analysis-loading-status analysis-loading-status--sidebar"
        data-testid="analysis-loading-status"
      >
        <v-progress-circular
          indeterminate
          size="16"
          width="2"
          color="primary"
          class="mr-2"
        />
        <span class="text-body-2">
          {{ t('analysis.readiness.inlineDescription', { sections: loadingSectionLabels }) }}
        </span>
      </div>
    </nav>
  </aside>

  <div class="analysis-section-mobile-tabs d-md-none">
    <v-tabs
      dark
      :model-value="modelValue"
      class="analysis-tabs"
      density="compact"
      fixed-tabs
      @update:model-value="selectTab"
    >
      <v-tab
        v-for="(tab, index) in tabs"
        :key="index"
        :value="index"
        class="tab-item"
      >
        <v-icon :icon="tab.icon" class="mb-1" />
        <span class="tab-label">{{ tab.title }}</span>
      </v-tab>
    </v-tabs>

    <div
      v-if="showLoadingStatus"
      class="analysis-loading-status analysis-loading-status--mobile"
      data-testid="analysis-loading-status"
    >
      <v-progress-circular
        indeterminate
        size="16"
        width="2"
        color="primary"
        class="mr-2"
      />
      <span class="text-body-2">
        {{ t('analysis.readiness.inlineDescription', { sections: loadingSectionLabels }) }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'

defineProps({
  modelValue: {
    type: Number,
    required: true,
  },
  tabs: {
    type: Array,
    required: true,
  },
  showLoadingStatus: {
    type: Boolean,
    default: false,
  },
  loadingSectionLabels: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue'])
const { t } = useI18n()

const selectTab = (index) => {
  emit('update:modelValue', index)
}
</script>

<style scoped>
.analysis-section-sidebar {
  align-self: stretch;
}

.analysis-section-sidebar__inner {
  position: sticky;
  top: 92px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--surface);
  box-shadow: var(--shadow-sm);
}

.analysis-section-link {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  font: inherit;
  font-weight: var(--font-weight-semibold);
  text-align: left;
  cursor: pointer;
}

.analysis-section-link:hover,
.analysis-section-link:focus-visible {
  background: var(--surface-soft);
  color: var(--text-primary);
  outline: none;
}

.analysis-section-link--active {
  border-color: rgba(165, 106, 67, 0.24);
  background: rgba(165, 106, 67, 0.12);
  color: var(--text-primary);
}

.analysis-section-link__icon {
  flex: 0 0 auto;
  color: rgb(var(--v-theme-primary));
}

.analysis-section-link__label {
  min-width: 0;
  line-height: 1.2;
}

.analysis-section-mobile-tabs {
  position: sticky;
  top: 0;
  z-index: 20;
  background-color: rgb(var(--v-theme-surface));
}

.analysis-tabs {
  background-color: rgb(var(--v-theme-surface));
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.v-tab) {
  height: 92px;
  padding: 16px 4px;
  color: var(--v-theme-on-surface);
  font-size: calc(var(--font-size-caption) - 0.125rem);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0;
  text-transform: none;
}

:deep(.v-tab .v-icon) {
  width: 28px;
  height: 28px;
  margin: 0 !important;
  font-size: 28px;
}

:deep(.tab-label) {
  display: block;
  max-width: 72px;
  font-weight: var(--font-weight-regular);
  line-height: 1.1;
  text-align: center;
  white-space: normal;
}

:deep(.v-tab--selected) {
  border-radius: 4px;
  background-color: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
  font-weight: var(--font-weight-medium);
}

.analysis-loading-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-primary), 0.08);
  color: var(--text-secondary);
}

.analysis-loading-status--sidebar {
  margin-top: 8px;
  align-items: flex-start;
}

.analysis-loading-status--mobile {
  display: flex;
  margin-top: 12px;
  border-radius: 14px;
  padding: 10px 12px;
}
</style>
